import { z } from 'zod';
import { describeAnswer } from './answers.js';
import { canonicalJson } from './json.js';
import { can } from './rbac.js';
import { checkAnswer, fromAgentValue } from './questions.js';
/**
 * Autofill (tracker task 35): answers read from documents become suggestions, never answers by
 * themselves (docs/design/chat-ux.md: anything read from a document is confirmed first). A person accepts
 * one, and it is recorded on the document channel as DOCUMENT_VERIFIED with its page and quote as
 * evidence. Comparing a suggestion with the current answer finds conflicts.
 */
/** Documents' answers are recorded as verified when the insurer's staff accept them. */
export const DOCUMENT_PROVENANCE = 'DOCUMENT_VERIFIED';
/**
 * The provenance an accepted suggestion is recorded with. A business accepting what was read from a document
 * it supplied itself is not independent verification (security review, task 49), so it counts as extracted.
 */
export function suggestionProvenance(perms) {
    // Who can verify a document's answer: the insurer's own reviewers, not the business it describes.
    return can(perms, 'reviews.edit') ? DOCUMENT_PROVENANCE : 'AI_EXTRACTED';
}
/** Below this, a reading is too unsure to put in front of anyone. */
export const SUGGESTION_MIN_CONFIDENCE = 0.5;
/** Which of a document's extracted answers can be suggested, in the question's own value format. */
export function documentSuggestions(questions, answers) {
    const rows = [];
    const rejected = [];
    const seen = new Set();
    for (const a of answers) {
        const q = questions[a.key];
        const no = (reason) => rejected.push({ question: a.key, reason });
        if (!q) {
            no('not a question in this assessment');
            continue;
        }
        if (seen.has(a.key)) {
            no('a second answer to the same question');
            continue;
        }
        if (a.confidence < SUGGESTION_MIN_CONFIDENCE) {
            no(`confidence ${a.confidence} is below ${SUGGESTION_MIN_CONFIDENCE}`);
            continue;
        }
        const check = checkAnswer(q, fromAgentValue(q, a.value), DOCUMENT_PROVENANCE);
        if (!check.ok) {
            no(`not a valid answer: ${check.errors.map((e) => e.message).join('; ')}`);
            continue;
        }
        seen.add(a.key);
        rows.push({
            question: a.key,
            value: check.value,
            confidence: a.confidence,
            page: a.page,
            quote: a.quote,
        });
    }
    return { rows, rejected };
}
export function suggestionKind(current, value) {
    if (!current || current.cleared)
        return 'fill';
    return canonicalJson(current.value) === canonicalJson(value) ? 'same' : 'conflict';
}
export const SUGGESTION_STATUSES = ['open', 'accepted', 'declined'];
/** The value in plain words. A stored value that no longer fits its question is shown raw, not dropped. */
export function suggestionLabel(q, value) {
    try {
        return describeAnswer(q, value);
    }
    catch {
        return typeof value === 'string' ? value : JSON.stringify(value);
    }
}
/** Accepting a suggestion: optionally the answer the person saw, so a newer one isn't overwritten. */
export const AcceptSuggestion = z.strictObject({
    based_on: z.string().uuid().nullable().optional(),
});
/** A reading this sure can be taken together with others in one click (task 106, decision 0074). */
export const CONFIDENT_AT = 0.8;
/**
 * Whether a suggestion can be accepted together with others: an open answer for an unanswered question
 * (or one that agrees with the answer given), that no other document disputes, read with confidence. A
 * mandatory or kill-switch question is still decided on its own (decision 0020), and a conflict always is.
 */
export function takesTogether(s) {
    return (s.status === 'open' &&
        s.kind !== 'conflict' &&
        s.disagreeing.length === 0 &&
        !s.mandatory &&
        !s.kill_switch &&
        s.confidence >= CONFIDENT_AT);
}
/** The suggestions to accept together: one per question, in the order given. */
export function together(list) {
    const seen = new Set();
    return list.filter((s) => takesTogether(s) && !seen.has(s.question) && !!seen.add(s.question));
}
/** Accepting several at once: the ones the person saw listed. Any that no longer qualify are left open. */
export const AcceptSuggestions = z.strictObject({
    ids: z.array(z.string().uuid()).min(1).max(200),
});
//# sourceMappingURL=suggestions.js.map